import React, { useCallback, useState } from 'react';

import FileUploadIcon from '@mui/icons-material/FileUpload';
import {
    Box,
    Button,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    Tooltip,
} from '@sunwisesoftware/sunwise-ui';
import i18next from 'i18next';
import isArray from 'lodash/isArray';
import isString from 'lodash/isString';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { Controller } from 'react-hook-form';

import LabelError from 'common/components/LabelError';
import { MAX_FILE_SIZE } from 'common/constants';
import { isImageBase64, isImageUrl } from 'common/utils/helpers';
import showToast from 'common/utils/showToast';

const ReactHookFormFile = ({
    accept = '*',
    contentEndAdornment,
    control,
    disabled,
    endAdornmentIcon = <FileUploadIcon />,
    endAdornmentTooltip = i18next.t('File upload'),
    fullWidth = false,
    handleClickButtonEndAdornment,
    id,
    label = i18next.t('File'),
    maxSize = MAX_FILE_SIZE,
    maxSizeErrorMessage = i18next.t(
        'The file exceeds the allowable size limit',
    ),
    name,
    onChange,
    placeholder = i18next.t('File upload'),
    setValue,
    text = i18next.t('Change image'),
    variant,
    visible,
}) => {
    if (!visible) return null;

    const [errorSize, setErrorSize] = useState(null);

    const onDrop = useCallback((acceptedFiles) => {
        if (!acceptedFiles.length) return;
        if (acceptedFiles.length > 0 && acceptedFiles[0].size >= maxSize) {
            showToast({
                body: maxSizeErrorMessage,
                type: 'error',
                autoClose: 3000,
            });
            setErrorSize(maxSizeErrorMessage);
            return;
        }

        setErrorSize(null);

        setValue(
            name,
            acceptedFiles.map((file) =>
                Object.assign(file, {
                    preview: URL.createObjectURL(file),
                }),
            ),
        );

        onChange ? onChange(acceptedFiles) : null;
    }, []);

    const propId = id ? id : 'inputGroup' + Math.random();
    const { getRootProps, getInputProps } = useDropzone({ onDrop });

    return (
        <Controller
            control={control}
            name={name}
            render={({ field: { value }, fieldState: { error } }) => (
                <Box {...getRootProps()} disabled={disabled}>
                    <label htmlFor={`contained-button-file-${propId}`}>
                        <input
                            {...getInputProps()}
                            accept={accept}
                            disabled={disabled}
                            id={propId}
                            type="file"
                            value={value?.name}
                        />
                        <FormControl
                            error={Boolean(error)}
                            fullWidth={fullWidth}
                        >
                            {label && variant !== 'profile' && (
                                <InputLabel htmlFor={propId}>
                                    {label}
                                </InputLabel>
                            )}

                            {variant !== 'profile' ? (
                                <OutlinedInput
                                    disabled={disabled}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            {contentEndAdornment ? (
                                                contentEndAdornment
                                            ) : (
                                                <Tooltip
                                                    title={endAdornmentTooltip}
                                                    placement="top"
                                                >
                                                    <IconButton
                                                        aria-label="File input adornment"
                                                        onClick={(e) => {
                                                            if (
                                                                handleClickButtonEndAdornment
                                                            ) {
                                                                e.stopPropagation();
                                                                handleClickButtonEndAdornment();
                                                            }
                                                        }}
                                                        edge="end"
                                                    >
                                                        {endAdornmentIcon}
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </InputAdornment>
                                    }
                                    id={propId}
                                    label={label}
                                    readOnly
                                    value={
                                        isString(value) &&
                                        !isImageBase64(value) &&
                                        !isImageUrl(value)
                                            ? value
                                            : isArray(value) && value.length > 0
                                              ? value[0].path
                                              : placeholder
                                    }
                                />
                            ) : (
                                <Button
                                    disabled={disabled}
                                    height="48px"
                                    size="large"
                                    type="button"
                                    variant="outlined"
                                >
                                    {text}
                                </Button>
                            )}

                            {(Boolean(error) || errorSize !== null) && (
                                <LabelError type="error">
                                    {error?.message}
                                    <br />
                                    {errorSize}
                                </LabelError>
                            )}
                        </FormControl>
                    </label>
                </Box>
            )}
        />
    );
};

ReactHookFormFile.defaultProps = {
    disabled: false,
    sxButton: {},
    variantButton: 'contained',
    visible: true,
};

ReactHookFormFile.propTypes = {
    accept: PropTypes.string,
    buttonText: PropTypes.string,
    classNameGroup: PropTypes.string,
    contentEndAdornment: PropTypes.string,
    control: PropTypes.object,
    disabled: PropTypes.bool,
    endAdornmentIcon: PropTypes.node,
    endAdornmentTooltip: PropTypes.string,
    error: PropTypes.object,
    fullWidth: PropTypes.bool,
    handleClickButtonEndAdornment: PropTypes.func,
    id: PropTypes.string,
    input: PropTypes.object,
    label: PropTypes.string,
    maxSize: PropTypes.number,
    maxSizeErrorMessage: PropTypes.string,
    meta: PropTypes.object,
    name: PropTypes.string,
    onChange: PropTypes.func,
    placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setValue: PropTypes.func,
    sxButton: PropTypes.object,
    text: PropTypes.string,
    variant: PropTypes.string,
    variantButton: PropTypes.string,
    visible: PropTypes.bool,
};

export default ReactHookFormFile;
